import React from 'react';
import { Grid, List, ListItem, ListItemText, MenuItem, Select, Tooltip } from '@mui/material';

import { useSettings, Utils } from 'app/main/components';
import { calcUnitPrice, getOrderQuantities } from './OrdersLinesTableUtils';

interface QuantitySelectCellTooltipProps {
	children: React.ReactNode;
	open: boolean;
	pricingLots?: any[];
	discountPercentage?: number;
}

interface QuantitySelectCellProps {
	quantity?: number;
	pricingsLots?: any[];
	multiples?: number;
	minQty?: number;
	discountPercentage?: number;
	handleChange: (newQuantity: number) => void;
}

export function QuantitySelectCellTooltip(props: QuantitySelectCellTooltipProps) {
	const { t } = useSettings();

	return (
		<Tooltip
			className="p-0"
			open={(props.pricingLots?.length || 0) > 1 && props.open}
			title={
				<List className="w-200" dense disablePadding>
					<ListItem disableGutters divider>
						<ListItemText>
							<Grid container justifyContent="space-between" spacing={4}>
								<Grid size="grow">
									<b>{t('DETAILS_ORDERS_LINES_LOTS_MIN_QTY')}</b>
								</Grid>
								<Grid size="grow" className="text-right">
									<b>{t('DETAILS_ORDERS_LINES_LOTS_UNIT_PRICE')}</b>
								</Grid>
							</Grid>
						</ListItemText>
					</ListItem>
					{props.pricingLots?.map((lot, index) => (
						<ListItem key={index} disableGutters divider={index !== (props.pricingLots?.length || 0) - 1}>
							<ListItemText>
								<Grid container justifyContent="space-between" spacing={4}>
									<Grid size="grow">{lot.itpr_qty_min}+</Grid>
									<Grid size="grow" className="text-right">
										{Utils.formatCurrency(
											calcUnitPrice(
												lot.itpr_base_price,
												lot.itpr_discountable ? props.discountPercentage || 0 : 0
											)
										)}
									</Grid>
								</Grid>
							</ListItemText>
						</ListItem>
					))}
				</List>
			}
			disableFocusListener
			disableHoverListener
			disableTouchListener
			placement="right-start"
			arrow
		>
			<div className="w-full">{props.children}</div>
		</Tooltip>
	);
}

function QuantitySelectCell(props: QuantitySelectCellProps) {
	const [isSelectOpen, setIsSelectOpen] = React.useState(false);

	if (props.quantity) {
		const quantities = getOrderQuantities(props.multiples, props.minQty);

		return (
			<QuantitySelectCellTooltip
				open={isSelectOpen}
				pricingLots={props.pricingsLots}
				discountPercentage={props.discountPercentage}
			>
				<Select
					classes={{ select: 'p-8' }}
					variant="filled"
					size="small"
					fullWidth
					value={props.quantity}
					onChange={event => props.handleChange(Number.parseInt(`${event.target.value}`))}
					open={isSelectOpen}
					onOpen={() => setIsSelectOpen(true)}
					onClose={() => setIsSelectOpen(false)}
					disableUnderline
					displayEmpty
				>
					{quantities.map(quantity => (
						<MenuItem key={quantity} value={quantity}>
							{quantity}
						</MenuItem>
					))}
				</Select>
			</QuantitySelectCellTooltip>
		);
	}

	return <></>;
}

export default React.memo(QuantitySelectCell);
